import { useCallback, useEffect, useState } from "react";
import { CalendarOff, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const fmt = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });

export const DiscoveryBlackouts = ({ onChanged }) => {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState({ date: "", end_date: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/discovery-blackouts").then((r) => setRows(r.data.blackouts)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/discovery-blackouts", form);
      toast.success(data.affected_confirmed
        ? `Blackout added — ${data.affected_confirmed} confirmed session(s) fall in this range; move them from the table below.`
        : "Blackout added — those dates no longer appear for booking");
      setForm({ date: "", end_date: "", reason: "" });
      load(); onChanged?.();
    } catch (ex) { toast.error(ex.response?.data?.detail || "Couldn't add blackout"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/discovery-blackouts/${id}`); toast.success("Blackout removed"); load(); onChanged?.(); }
    catch { toast.error("Couldn't remove blackout"); }
  };

  const INPUT = "rounded-md border border-neutral-700 bg-neutral-950 px-2.5 py-1.5 text-xs text-white outline-none focus:border-[#0A8B85]";
  return (
    <section className="mb-6 rounded-xl border border-neutral-800 bg-neutral-900/60 p-4" data-testid="discovery-blackouts">
      <div className="flex items-center gap-2">
        <CalendarOff className="h-4 w-4 text-[#E3B84C]" />
        <h2 className="text-sm font-extrabold text-white">Blackout dates</h2>
        <span className="text-xs text-neutral-500">— holidays, travel. Slots on these dates roll to the next available week.</span>
      </div>
      <form onSubmit={add} className="mt-3 flex flex-wrap items-end gap-2">
        <label className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400">From
          <input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={`mt-1 block ${INPUT}`} data-testid="blackout-date" /></label>
        <label className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400">To <span className="normal-case text-neutral-500">(optional)</span>
          <input type="date" value={form.end_date} min={form.date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className={`mt-1 block ${INPUT}`} data-testid="blackout-end-date" /></label>
        <label className="flex-1 min-w-[180px] text-[11px] font-semibold uppercase tracking-wide text-neutral-400">Reason
          <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="e.g. Public holiday, travel" className={`mt-1 block w-full ${INPUT}`} data-testid="blackout-reason" /></label>
        <button type="submit" disabled={busy || !form.date} className="inline-flex items-center gap-1 rounded-md bg-[#0A8B85] px-3 py-1.5 text-xs font-bold text-white disabled:opacity-40" data-testid="blackout-add">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Block dates
        </button>
      </form>
      <div className="mt-3 flex flex-wrap gap-2" data-testid="blackout-list">
        {rows === null ? <Loader2 className="h-4 w-4 animate-spin text-neutral-500" />
          : rows.length === 0 ? <p className="text-xs text-neutral-500" data-testid="blackout-empty">No blackout dates set.</p>
          : rows.map((b) => (
            <span key={b.blackout_id} className="inline-flex items-center gap-2 rounded-full border border-neutral-700 bg-neutral-950 px-3 py-1 text-xs text-neutral-200" data-testid={`blackout-${b.blackout_id}`}>
              <span className="font-semibold">{fmt(b.date)}{b.end_date && b.end_date !== b.date ? ` → ${fmt(b.end_date)}` : ""}</span>
              {b.reason && <span className="text-neutral-500">· {b.reason}</span>}
              <button type="button" onClick={() => remove(b.blackout_id)} aria-label="Remove blackout" className="text-neutral-500 hover:text-red-400" data-testid={`blackout-remove-${b.blackout_id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
      </div>
    </section>
  );
};
